// src/index.ts

// ========================= Types =========================

export type SpaceCustomization = {
  id: string; // Space identifier
  color?: string; // Optional fill color (hex)
  opacity?: number; // Optional opacity value (0-1)
  border?: {
    color: string; // Border color (hex)
    lineWidth?: number; // Optional border width in pixels
  };
};

export type FloorPlanEvents =
  | "click"
  | "right-click"
  | "double-click"
  | "hover"
  | "blur"
  | "plan-right-click"
  | "outside-click";

export type FloorPlanEntityType = "SPACE" | "WALL_PERIMETER" | "ROOM" | "AREA";

export interface FloorplanConfig {
  /** ID of the container element to mount the iframe into */
  key: string;

  /** Base URL of the hosted floorplan application (iframe src) */
  url?: string;

  /** Credentials & scoping */
  access_token: string;
  location_id: string;
  layer_id?: string;

  /** Optional entity filters */
  spaces?: (string | SpaceCustomization)[];
  room_id?: string;
  zone_id?: string;

  /** Display & behavior */
  class_names?: string;
  title?: string;
  dark_mode?: boolean;

  /** Event allowlists */
  events?: FloorPlanEvents[];
  entity_events?: FloorPlanEntityType[];

  /** Optional custom loader identifier */
  custom_loader?: string;
  zoom?: boolean;
  primary_color?: string;
}

export type Center = {
  x: number;
  y: number;
  relativeX?: number;
  relativeY?: number;
};

export interface FloorPlanEntity<T extends Record<string, any> = {}> {
  id: string;
  type: FloorPlanEntityType;
  center?: Center;
  properties?: T & { locationId: string; layerId: string };
}

type SpaceMetadata = any;
type RoomMetadata = any;
type WallPerimeterMetadata = any;

export type FloorPlanEntityMetadata =
  | SpaceMetadata
  | RoomMetadata
  | WallPerimeterMetadata;

export type FloorPlanEventPayload = {
  event: FloorPlanEvents;
  id?: string; // entity id (e.g., Space, Room), may be absent on non-entity events
  metadata?: FloorPlanEntity<FloorPlanEntityMetadata>;
  center?: Center; // used for plan-right-click / scene events
};

export interface SpaceClickedEvent {
  event: "SPACE_CLICKED";
  space_id: string;
}

export interface FloorPlanEntityEvent {
  event: "FLOOR_PLAN_ENTITY";
  data: FloorPlanEventPayload;
}

export type SDKEvent = "SPACE_CLICKED" | "FLOOR_PLAN_ENTITY";

export type EventCallback = (
  event: SpaceClickedEvent | FloorPlanEntityEvent
) => void;

// ========================= SDK =========================

export class FloorplanSDK {
  private iframe: HTMLIFrameElement | null = null;
  private config: FloorplanConfig;
  private eventListeners: Map<SDKEvent, EventCallback[]> = new Map();
  private boundHandleMessage: (e: MessageEvent) => void;

  constructor(config: FloorplanConfig) {
    this.config = {
      url: config.url ?? "https://floorplan.gospace.app",
      key: config.key,
      access_token: config.access_token,
      location_id: config.location_id,
      layer_id: config.layer_id,
      spaces: config.spaces,
      room_id: config.room_id,
      zone_id: config.zone_id,
      class_names: config.class_names ?? "w-full h-full",
      title: config.title ?? "gospace floorplan",
      dark_mode: config.dark_mode ?? true,
      events: config.events,
      entity_events: config.entity_events ?? ["SPACE"],
      custom_loader: config.custom_loader,
      zoom: config.zoom,
      primary_color: config.primary_color,
    };

    // Validate required
    if (!this.config.key || !this.config.access_token || !this.config.location_id) {
      throw new Error(
        "Missing required configuration: key, access_token, and location_id are required"
      );
    }

    const idRegex = /^[A-Za-z0-9_-]+$/;
    const hexColor = /^#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

    // Validate spaces: strings or SpaceCustomization objects
    if (this.config.spaces) {
      if (!Array.isArray(this.config.spaces)) {
        throw new Error("Invalid spaces: must be an array of strings or customization objects");
      }
      for (const item of this.config.spaces) {
        if (typeof item === "string") {
          if (!idRegex.test(item)) {
            throw new Error(`Invalid space id "${item}": expected [A-Za-z0-9_-]+`);
          }
        } else if (item && typeof item === "object") {
          if (!item.id || !idRegex.test(item.id)) {
            throw new Error(`Invalid space customization id "${item?.id}": expected [A-Za-z0-9_-]+`);
          }
          if (item.color && !hexColor.test(item.color)) {
            throw new Error(`Invalid color "${item.color}" for space "${item.id}"`);
          }
          if (item.opacity != null && (item.opacity < 0 || item.opacity > 1)) {
            throw new Error(`Invalid opacity ${item.opacity} for space "${item.id}" (0..1)`);
          }
          if (item.border) {
            if (!hexColor.test(item.border.color)) {
              throw new Error(`Invalid border color "${item.border.color}" for space "${item.id}"`);
            }
            if (item.border.lineWidth != null && item.border.lineWidth < 0) {
              throw new Error(`Invalid border lineWidth ${item.border.lineWidth} for space "${item.id}"`);
            }
          }
        } else {
          throw new Error("Invalid spaces item; expected string or SpaceCustomization");
        }
      }
    }

    // Validate optional ids
    if (this.config.room_id && !idRegex.test(this.config.room_id)) {
      throw new Error("Invalid room_id: must match [A-Za-z0-9_-]+");
    }
    if (this.config.zone_id && !idRegex.test(this.config.zone_id)) {
      throw new Error("Invalid zone_id: must match [A-Za-z0-9_-]+");
    }

    this.boundHandleMessage = this.handleMessage.bind(this);
  }

  public init(): void {
    const container = document.getElementById(this.config.key);
    if (!container) {
      throw new Error(`Container with ID ${this.config.key} not found`);
    }

    this.iframe = document.createElement("iframe");
    this.iframe.src = this.config.url!;
    this.iframe.className = this.config.class_names ?? "w-full h-full";
    this.iframe.title = this.config.title ?? "gospace floorplan";

    // (optional) Restrictive sandbox; adjust if embedded app needs more permissions
    // this.iframe.sandbox.add("allow-scripts");
    // this.iframe.sandbox.add("allow-same-origin");

    container.appendChild(this.iframe);

    this.iframe.addEventListener("load", () => {
      if (!this.iframe || !this.iframe.contentWindow) return;
      this.sendCredentials();
    });

    window.addEventListener("message", this.boundHandleMessage);
  }

  public update(
    partial: Partial<
      Pick<
        FloorplanConfig,
        | "layer_id"
        | "spaces"
        | "room_id"
        | "zone_id"
        | "dark_mode"
        | "events"
        | "entity_events"
        | "custom_loader"
      >
    >
  ): void {
    this.config = { ...this.config, ...partial };
    this.sendCredentials();
  }

  public on(eventType: SDKEvent, callback: EventCallback): void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, []);
    }
    this.eventListeners.get(eventType)!.push(callback);
  }

  public off(eventType: SDKEvent, callback: EventCallback): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      this.eventListeners.set(
        eventType,
        listeners.filter((cb) => cb !== callback)
      );
    }
  }

  public destroy(): void {
    window.removeEventListener("message", this.boundHandleMessage);
    if (this.iframe && this.iframe.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe);
      this.iframe = null;
    }
    this.eventListeners.clear();
  }

  // ========================= Internals =========================

  private sendCredentials(): void {
    const target = this.iframe?.contentWindow;
    if (!target) return;
    const origin = new URL(this.config.url!).origin;

    target.postMessage(
      {
        access_token: this.config.access_token,
        location_id: this.config.location_id,
        layer_id: this.config.layer_id,
        spaces: this.config.spaces,
        room_id: this.config.room_id,
        zone_id: this.config.zone_id,
        dark_mode: this.config.dark_mode,
        events: this.config.events,
        entity_events: this.config.entity_events,
        custom_loader: this.config.custom_loader,
        zoom: this.config.zoom,
        primary_color: this.config.primary_color,
      },
      origin
    );
  }

  private handleMessage(event: MessageEvent): void {
    const expectedOrigin = new URL(this.config.url!).origin;

    // Enforce origin & source (security)
    if (event.origin !== expectedOrigin) return;
    if (event.source !== this.iframe?.contentWindow) return;

    const data = event.data;

    // Handshake: iframe indicates ready state
    if (data === "ready") {
      this.sendCredentials();
      return;
    }

    if (!data || typeof data !== "object") return;

    const payload = data as FloorPlanEventPayload;

    // Normalize right-click naming for host compatibility
    const normalizedEvent: FloorPlanEvents | undefined =
      payload.event === "plan-right-click" &&
      (this.config.events?.includes("outside-click") ?? false)
        ? "outside-click"
        : payload.event;

    if (!normalizedEvent) return;

    // Event allowlist (if provided)
    const eventAllowed =
      !this.config.events || this.config.events.includes(normalizedEvent);

    // Entity allowlist (if provided)
    const entityType = payload.metadata?.type as FloorPlanEntityType | undefined;
    const entityAllowed =
      !this.config.entity_events ||
      (entityType ? this.config.entity_events.includes(entityType) : true);

    // Dispatch FLOOR_PLAN_ENTITY if allowed
    if (eventAllowed && entityAllowed) {
      const listeners = this.eventListeners.get("FLOOR_PLAN_ENTITY") || [];
      if (listeners.length) {
        const forwarded: FloorPlanEventPayload = {
          ...payload,
          event: normalizedEvent,
        };
        listeners.forEach((cb) =>
          cb({ event: "FLOOR_PLAN_ENTITY", data: forwarded })
        );
      }
    }

    // Legacy: SPACE_CLICKED on click of SPACE with sane id
    if (
      normalizedEvent === "click" &&
      entityType === "SPACE" &&
      typeof payload.id === "string" &&
      /^[A-Za-z0-9_-]+$/.test(payload.id)
    ) {
      const listeners = this.eventListeners.get("SPACE_CLICKED") || [];
      if (listeners.length) {
        listeners.forEach((cb) =>
          cb({ event: "SPACE_CLICKED", space_id: payload.id! })
        );
      }
    }
  }
}
